import actions from './actions';
import { ClientDocument, DocumentFolder } from '../../components/library/documents/types';

export interface DocumentsState {
  documents: ClientDocument[];
  folders: DocumentFolder[];
  isLoading: boolean;
  isUploading: boolean;
  uploadPending: number;
  isCreatingFolder: boolean;
  error: string | null;
}

const initState: DocumentsState = {
  documents: [],
  folders: [],
  isLoading: false,
  isUploading: false,
  uploadPending: 0,
  isCreatingFolder: false,
  error: null,
};

// Collect the id of `rootId` plus every folder that descends from it (by parentId).
function collectFolderSubtreeIds(rootId: string, folders: DocumentFolder[]): Set<string> {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) {
        ids.add(f.id);
        grew = true;
      }
    }
  }
  return ids;
}

export default function reducer(
  state: DocumentsState = initState,
  { type, payload }: { type: string; payload?: any }
): DocumentsState {
  switch (type) {
    case actions.LOAD_DOCUMENTS:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case actions.LOAD_DOCUMENTS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        documents: payload.documents,
      };
    case actions.LOAD_DOCUMENTS_ERROR:
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    case actions.UPLOAD_DOCUMENT:
      return {
        ...state,
        isUploading: true,
        uploadPending: state.uploadPending + 1,
      };
    case actions.UPLOAD_DOCUMENT_SUCCESS: {
      const pending = Math.max(0, state.uploadPending - 1);
      return {
        ...state,
        uploadPending: pending,
        isUploading: pending > 0,
        documents: [payload.document, ...state.documents],
      };
    }
    case actions.UPLOAD_DOCUMENT_ERROR: {
      const pending = Math.max(0, state.uploadPending - 1);
      return {
        ...state,
        uploadPending: pending,
        isUploading: pending > 0,
        error: payload.error,
      };
    }
    case actions.DELETE_DOCUMENT_SUCCESS:
      return {
        ...state,
        documents: state.documents.filter((d) => d.id !== payload.id),
      };
    case actions.DELETE_DOCUMENT_ERROR:
      return {
        ...state,
        error: payload.error,
      };
    case actions.LOAD_FOLDERS_SUCCESS:
      return {
        ...state,
        folders: payload.folders,
      };
    case actions.LOAD_FOLDERS_ERROR:
      return {
        ...state,
        error: payload.error,
      };
    case actions.CREATE_FOLDER:
      return {
        ...state,
        isCreatingFolder: true,
        error: null,
      };
    case actions.CREATE_FOLDER_SUCCESS:
      return {
        ...state,
        isCreatingFolder: false,
        folders: [...state.folders, payload.folder],
      };
    case actions.CREATE_FOLDER_ERROR:
      return {
        ...state,
        isCreatingFolder: false,
        error: payload.error,
      };
    case actions.DELETE_FOLDER_SUCCESS: {
      // Remove the folder plus every descendant folder, and any documents that
      // lived in any of those folders — the saga has already deleted them remotely.
      const removedFolderIds = collectFolderSubtreeIds(payload.folderId, state.folders);
      return {
        ...state,
        folders: state.folders.filter((f) => !removedFolderIds.has(f.id)),
        documents: state.documents.filter(
          (d) => !(d.folderId && removedFolderIds.has(d.folderId))
        ),
      };
    }
    case actions.DELETE_FOLDER_ERROR:
      return {
        ...state,
        error: payload.error,
      };
    default:
      return state;
  }
}
